import { graphql, PageProps } from 'gatsby';
import React from 'react';
import FranchiseBlogSection from '../../components/blocks/franchise-blog-section';
import FranchiseGlobalAbout from '../../components/blocks/FranchiseGlobalAbout';
import FranchiseReviewsSlider from '../../components/sections/FranchiseReviewsSlider';
import useServiceTypeBlockFilter from '../../hooks/use-service-type-block-filter';
import DefaultTemplate from '../default';
import transformDynamic from './utils/transform-dynamic';
import type { FranchiseData } from '../../sourcing/source-nodes/sources/franchises';

const FranchisePageTemplate = ({ data, pageContext, ...props }: PageProps): JSX.Element => {
    const { testimonialCategories, blogPostCategories } = pageContext;

    const testimonials = data?.allFranchiseTestimonial?.nodes || [];
    const blogPosts = data.allFranchiseBlogPost.nodes || [];

    const sortedTestimonials = testimonialCategories
        ? testimonials
            .sort((a, b) => ((testimonialCategories[a.category] ?? 99) - (testimonialCategories[b.category] ?? 99)))
            .slice(0, 9)
        : [];

    const sortedBlogPosts = blogPostCategories
        ? Object.entries(
            blogPosts
                .reduce((acc, val) => {
                    if (!acc[val.blogCategory]) {
                        acc[val.blogCategory] = [];
                    }
                    acc[val.blogCategory].push(val);
                    return acc;
                }, {}),
        )
            .map(([key, posts]) => [
                key,
                posts.sort((a, b) => b.blogPublishedDate.localeCompare(a.blogPublishedDate)),
            ]) // sort posts
            .sort((a, b) => (blogPostCategories[a[0]] ?? 99) - (blogPostCategories[b[0]] ?? 99)) // sort keys
            .reduce((acc, val) => {
                if (acc.length < 3) {
                    acc = acc.concat(val[1].slice(0, 3));
                }
                return acc.slice(0, 3);
            }, [])
        : [];

    const {
        servicesProvided,
        name,
        activeMarkets,
        legacySiteEnabled,
        address,
        profileExcerpt,
    } = data.franchise as FranchiseData;

    const careerPlug = data?.franchise?.aboutUs?.careerPlugUrl;

    const reparsedData = useServiceTypeBlockFilter(transformDynamic(data, {
        locationName: name,
        locationCity: address?.city,
        locationState: address?.state,
        locationServiceAreas: (activeMarkets || []).map(activeMarket => `${activeMarket.city}, ${activeMarket.state}`)?.join(', '),
        locationCompanyProfile: profileExcerpt,
        jobLink: careerPlug,
        servicesProvided: (servicesProvided || []).map(x => x.serviceLine),
    }), 'cmsFranchisePageBuilder');

    const showAboutInFooter = reparsedData.cmsFranchisePageBuilder.show_about_in_footer;

    return (
        <DefaultTemplate
            data={{ data: reparsedData.cmsFranchisePageBuilder }}
            {...props}
        >
            {sortedTestimonials.length > 2 && <FranchiseReviewsSlider data={{ slides: sortedTestimonials }} />}
            {showAboutInFooter && <FranchiseGlobalAbout />}

            {showAboutInFooter && legacySiteEnabled && (
                <FranchiseBlogSection
                    data={sortedBlogPosts}
                />
            )}
        </DefaultTemplate>
    );
};

export const query = graphql`
    query ($slug: String!, $franchiseNumber: Int!) {
        allFranchiseBlogPost(filter: { franchiseNumber: { eq: $franchiseNumber } }) {
            nodes {
                blogTitle
                blogContent
                blogPermaLink
                blogCategory
                blogPublishedDate(formatString: "YYYYMMDD")
            }
        }
        allFranchiseUniqueContent(filter: {
            franchiseNumber: {
                eq: $franchiseNumber
            }
        }) {
            nodes {
                approvedDate
                pageName
                content
                rawContent
            }
        }
        franchise(franchiseNumber: { eq: $franchiseNumber }) {
            ...FranchiseMicrositeData
        }
        cmsFranchisePageBuilder(url: { eq: $slug }) {
            title
            url
            seo {
                meta_title
                meta_description
                meta_image {
                  url
                }
            }
            show_about_in_footer
            resolvedSections {
                show_below_if {
                    linked_service_type {
                        franchise_service_mapping
                    }
                }
                ...FranchiseCollapsibleAboutData
                ...FranchiseContactHeroData
                ...FranchiseAboutInfoData
                ...AboutFranchiseHeroData
                ...ServiceAreaData
                ...ServicesWeProvideData
                ...BlogrollData
                ...CommunityBannerData
                ...ContactBannerData
                ...FaqsData
                ...FranchiseDisclosureData
                ...GalleryData
                ...HeroData
                ...IconBulletsData
                ...IngredientsDisclosureData
                ...InsurerGridData
                ...MediaContentData
                ...MediacontentFaqsData
                ...FaqsGridData
                ...MetroDetailData
                ...MultiBlogrollData
                ...PartnerLogosData
                ...QuicklinksData
                ...ResourceDetailData
                ...ReviewGridData
                ...RichtextBlockData
                ...ServicesDetailData
                ...ServicesSliderData
                ...SliderData
                ...SplitTextBannerData
                ...SubnavData
                ...TeamMembersData
                ...TestimonialData
                ...TextBannerData
                ...FormData
                ...AnchorData
                ...FranchiseTeamMembersData
                ...FranchiseFormHeroData
                ...FranchiseFormCtaData
            }
        }
        allFranchiseTestimonial(filter: { franchiseNumber: { eq: $franchiseNumber }}) {
          nodes {
              name
              date
              state
              city
              category
              review
              zip
              franchiseNumber
          }
      }
    }
`;

export default FranchisePageTemplate;
